import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Textarea } from '@/components/ui/textarea';
import {
  Briefcase, Users, Mail, Building2, Search,
  Filter, X, CheckCircle, XCircle, Edit, Loader2, MapPin, Clock,
  Calendar, Star, Tag, GraduationCap, DollarSign,
} from 'lucide-react';
import { TECHNOLOGY_AREAS, TECHNOLOGY_AREA_LABEL_MAP } from '@/lib/constants';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Separator } from '@/components/ui/separator';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { api } from '@/lib/api';
import { useAdminEventFilter } from '@/hooks/useAdminEventFilter';
import {
  SEEKING_COLLABORATION_OPTIONS,
  SEEKING_LABEL_TO_ENUM,
  SECURITY_CLASSIFICATIONS,
  FUNDING_STATUS_V2,
} from '@/lib/constants';
import { getProjectSourceType, type ProjectSourceType } from '@/lib/projectSource';
import { toast } from 'sonner';
import { DesktopShell } from '@/components/desktop/DesktopShell';
import { ThreePanelLayout } from '@/components/desktop/layouts/ThreePanelLayout';

// ===========================
// Types
// ===========================

type ProjectStatus = 'pending' | 'approved' | 'rejected';
type ProjectStage = 'concept' | 'prototype' | 'pilot_ready' | 'deployed';

interface Project {
  id: string;
  title: string;
  description?: string;
  piId?: string;
  piRole?: string;
  department?: string;
  stage?: ProjectStage;
  classification?: string;
  researchAreas?: string[];
  keywords?: string[];
  students?: string[];
  seeking?: string[];
  demoSchedule?: string;
  interestedCount?: number;
  status: ProjectStatus;
  sponsorOrganization?: string;
  requirements?: string;
  benefits?: string;
  location?: string;
  duration?: string;
  deadline?: string;
  dodAlignment?: string[];
  dodImpact?: string;
  technologyFocus?: string[];
  fundingStatus?: string;
  featured?: boolean;
  pocUserId?: string;
  pocFirstName?: string;
  pocLastName?: string;
  pocEmail?: string;
  pocRank?: string;
  pocIsCheckedIn?: boolean;
  submittedBy?: string;
  createdAt?: string;
  updatedAt?: string;
  partnerId?: string | null;
  partner?: { id: string; name: string; organizationType: string } | null;
}

interface AdminProjectsResponse {
  success: boolean;
  data: Project[];
  counts?: Record<string, number>;
  pagination?: { total: number };
}

// ===========================
// Constants
// ===========================

const STATUS_STYLES: Record<ProjectStatus, { color: string; bg: string; label: string }> = {
  pending: { color: '#ca8a04', bg: 'bg-yellow-50 dark:bg-yellow-950', label: 'Pending' },
  approved: { color: '#16a34a', bg: 'bg-green-50 dark:bg-green-950', label: 'Approved' },
  rejected: { color: '#dc2626', bg: 'bg-red-50 dark:bg-red-950', label: 'Rejected' },
};

const STATUS_FILTER_OPTIONS: { value: ProjectStatus | 'all'; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'pending', label: 'Pending' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
];

const ALL_STAGES: ProjectStage[] = ['concept', 'prototype', 'pilot_ready', 'deployed'];

const STAGE_LABELS: Record<ProjectStage, string> = {
  concept: 'Concept',
  prototype: 'Prototype',
  pilot_ready: 'Pilot Ready',
  deployed: 'Deployed',
};


// ===========================
// Helpers
// ===========================

const SOURCE_TYPE_STYLES: Record<ProjectSourceType, { bg: string; icon: string; label: string }> = {
  NPS: { bg: 'bg-blue-600', icon: 'graduation-cap', label: 'NPS Project' },
  'Military/Gov': { bg: 'bg-sky-700 dark:bg-sky-600', icon: 'building', label: 'Military/Gov' },
  Industry: { bg: 'bg-slate-700 dark:bg-slate-600', icon: 'briefcase', label: 'Industry' },
};

function getTimeAgo(dateString: string) {
  const date = new Date(dateString);
  const now = new Date();
  const diffDays = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));
  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays} days ago`;
  return date.toLocaleDateString();
}

// ===========================
// Component
// ===========================

export default function ProjectsPage() {
  const { eventFilter, setEventFilter, events, fetchEvents, getResolvedEventId } = useAdminEventFilter();

  // Data state
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);

  // Filter state (all client-side for instant response)
  const [statusFilter, setStatusFilter] = useState<ProjectStatus | 'all'>('all');
  const [selectedSeeking, setSelectedSeeking] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedStages, setSelectedStages] = useState<string[]>([]);

  // Dialog state
  const [rejectDialog, setRejectDialog] = useState(false);
  const [editDialog, setEditDialog] = useState(false);


  const [actionLoading, setActionLoading] = useState(false);

  // Edit form state
  const [editForm, setEditForm] = useState({
    title: '',
    description: '',
    department: '',
    stage: '' as ProjectStage | '',
    classification: '',
    seeking: [] as string[],
    technologyFocus: [] as string[],
    fundingStatus: '',
    dodImpact: '',
    featured: false,
  });
  const [editSaving, setEditSaving] = useState(false);

  // Fetch projects (server-side event filtering, client-side for the rest)
  const fetchProjects = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      const eventId = getResolvedEventId();
      if (eventId) params.set('eventId', eventId);

      const response = await api.get<AdminProjectsResponse>(`/admin/projects?${params}`);
      if (response.success && response.data) {
        setProjects(response.data);
        if (selectedProject) {
          const updated = response.data.find((p) => p.id === selectedProject.id);
          if (updated) setSelectedProject(updated);
          else setSelectedProject(null);
        }
      }
    } catch {
      toast.error('Failed to load opportunities');
    } finally {
      setLoading(false);
    }
  }, [selectedProject]);

  useEffect(() => {
    fetchEvents().then(() => fetchProjects());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Refetch when event filter changes (skip initial mount — handled above)
  const didMountRef = useRef(false);
  useEffect(() => {
    if (!didMountRef.current) {
      didMountRef.current = true;
      return;
    }
    fetchProjects();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventFilter]);

  // Client-side filtering (all filters — instant response)
  const filteredProjects = useMemo(() => {
    return projects.filter((p) => {
      if (statusFilter !== 'all' && p.status !== statusFilter) return false;
      if (selectedSeeking.length > 0) {
        const seekingArray = p.seeking ?? [];
        const match = seekingArray.some((s) => {
          const normalized = SEEKING_LABEL_TO_ENUM[s] ?? s;
          return selectedSeeking.includes(normalized);
        });
        if (!match) return false;
      }
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase();
        if (
          !p.title.toLowerCase().includes(q) &&
          !(p.description || '').toLowerCase().includes(q) &&
          !(p.department || '').toLowerCase().includes(q)
        ) return false;
      }
      if (selectedStages.length > 0 && (!p.stage || !selectedStages.includes(p.stage))) return false;
      return true;
    });
  }, [projects, statusFilter, selectedSeeking, searchQuery, selectedStages]);

  // Clear selection when it's no longer in the visible list
  useEffect(() => {
    if (selectedProject && !filteredProjects.some((p) => p.id === selectedProject.id)) {
      setSelectedProject(null);
    }
  }, [filteredProjects, selectedProject]);

  const handleSelectProject = (project: Project) => {
    setSelectedProject(project);
  };

  // ===========================
  // Action Handlers
  // ===========================


  const handleApprove = async () => {
    if (!selectedProject) return;
    setActionLoading(true);
    try {
      await api.post(`/admin/projects/${selectedProject.id}/approve`, {});
      toast.success(`"${selectedProject.title}" approved`);
      await fetchProjects();
    } catch (error: unknown) {
      const err = error as { response?: { data?: { error?: string } } };
      toast.error(err.response?.data?.error || 'Failed to approve opportunity');
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async () => {
    if (!selectedProject) return;
    setActionLoading(true);
    try {
      await api.post(`/admin/projects/${selectedProject.id}/reject`);
      toast.success(`"${selectedProject.title}" rejected`);
      setRejectDialog(false);
      await fetchProjects();
    } catch (error: unknown) {
      const err = error as { response?: { data?: { error?: string } } };
      toast.error(err.response?.data?.error || 'Failed to reject opportunity');
    } finally {
      setActionLoading(false);
    }
  };

  const openEditDialog = (project: Project) => {
    setEditForm({
      title: project.title || '',
      description: project.description || '',
      department: project.department || '',
      stage: project.stage || '',
      classification: project.classification || '',
      seeking: project.seeking || [],
      technologyFocus: project.technologyFocus || [],
      fundingStatus: project.fundingStatus || '',
      dodImpact: project.dodImpact || '',
      featured: project.featured || false,
    });
    setEditDialog(true);
  };

  const handleEditSave = async () => {
    if (!selectedProject) return;
    setEditSaving(true);
    try {
      const data: Record<string, unknown> = {};
      if (editForm.title) data.title = editForm.title;
      if (editForm.description) data.description = editForm.description;
      if (editForm.department) data.department = editForm.department;
      if (editForm.stage) data.stage = editForm.stage;
      if (editForm.classification) data.classification = editForm.classification;
      data.seeking = editForm.seeking;
      data.technologyFocus = editForm.technologyFocus;
      if (editForm.fundingStatus) data.fundingStatus = editForm.fundingStatus;
      if (editForm.dodImpact) data.dodImpact = editForm.dodImpact;
      data.featured = editForm.featured;

      await api.put(`/admin/projects/${selectedProject.id}`, data);
      toast.success('Opportunity updated');
      setEditDialog(false);
      await fetchProjects();
    } catch (error: unknown) {
      const err = error as { response?: { data?: { error?: string } } };
      toast.error(err.response?.data?.error || 'Failed to update opportunity');
    } finally {
      setEditSaving(false);
    }
  };

  // ===========================
  // Filter helpers
  // ===========================

  const toggleStage = (stage: string) =>
    setSelectedStages((prev) =>
      prev.includes(stage) ? prev.filter((s) => s !== stage) : [...prev, stage]
    );

  const activeFilterCount =
    (searchQuery ? 1 : 0) +
    (statusFilter !== 'all' ? 1 : 0) +
    selectedSeeking.length +
    selectedStages.length;

  const toggleSeeking = (value: string) =>
    setSelectedSeeking((prev) =>
      prev.includes(value) ? prev.filter((s) => s !== value) : [...prev, value]
    );

  const clearFilters = () => {
    setSearchQuery('');
    setStatusFilter('all');
    setSelectedSeeking([]);
    setSelectedStages([]);
  };

  const pocName = selectedProject
    ? [selectedProject.pocFirstName, selectedProject.pocLastName].filter(Boolean).join(' ')
    : null;

  const pocEmail = selectedProject?.pocEmail;

  const sourceType = selectedProject ? getProjectSourceType(selectedProject) : null;
  const isNPS = sourceType === 'NPS';
  const isIndustry = sourceType === 'Industry';
  const sourceStyle = sourceType ? SOURCE_TYPE_STYLES[sourceType] : null;

  // ===========================
  // Left Panel — Filters
  // ===========================

  const FiltersPanel = (
    <div className="h-full flex flex-col">
      <div className="h-[72px] px-4 flex items-center justify-between border-b border-border bg-muted flex-shrink-0">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
            <Filter className="h-5 w-5 text-primary" />
          </div>
          <div>
            <h2 className="font-semibold text-lg">Filters</h2>
            <p className="text-sm text-muted-foreground">Refine your search</p>
          </div>
        </div>
        {activeFilterCount > 0 && (
          <Button variant="ghost" size="sm" onClick={clearFilters} className="h-7 text-[11px] px-2">
            Clear ({activeFilterCount})
          </Button>
        )}
      </div>

      <ScrollArea className="flex-1">
        <div className="px-3 py-3 space-y-3">
          {/* Event Filter */}
          <div>
            <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1.5 block">Event</Label>
            <RadioGroup value={eventFilter} onValueChange={(v) => { setEventFilter(v); setSelectedProject(null); }} className="gap-0.5">
              <div className="flex items-center gap-1.5 py-0.5">
                <RadioGroupItem value="all" id="event-all" className="h-3.5 w-3.5" />
                <label htmlFor="event-all" className="text-xs cursor-pointer">All Events</label>
              </div>
              <div className="flex items-center gap-1.5 py-0.5">
                <RadioGroupItem value="active" id="event-active" className="h-3.5 w-3.5" />
                <label htmlFor="event-active" className="text-xs cursor-pointer">Active Event</label>
              </div>
              {events
                .filter((ev) => ev.status === 'archived')
                .sort((a, b) => new Date(b.dateStart).getTime() - new Date(a.dateStart).getTime())
                .map((ev) => (
                  <div key={ev.id} className="flex items-center gap-1.5 py-0.5">
                    <RadioGroupItem value={ev.id} id={`event-${ev.id}`} className="h-3.5 w-3.5" />
                    <label htmlFor={`event-${ev.id}`} className="text-xs cursor-pointer flex items-center justify-between w-full">
                      <span className="truncate">{ev.name}</span>
                      <span className="text-[10px] text-muted-foreground shrink-0 ml-1">
                        {new Date(ev.dateStart).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}
                      </span>
                    </label>
                  </div>
                ))}
            </RadioGroup>
          </div>

          <Separator />

          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
            <Input
              placeholder="Title, description..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-8 h-8 text-xs"
            />
            {searchQuery && (
              <Button variant="ghost" size="icon" className="absolute right-0.5 top-1/2 -translate-y-1/2 h-6 w-6" onClick={() => setSearchQuery('')}>
                <X className="w-3.5 h-3.5" />
              </Button>
            )}
          </div>

          <Separator />

          {/* Status Filter */}
          <div>
            <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1.5 block">Status</Label>
            <RadioGroup value={statusFilter} onValueChange={(v) => setStatusFilter(v as ProjectStatus | 'all')} className="gap-0.5">
              {STATUS_FILTER_OPTIONS.map(({ value, label }) => (
                <div key={value} className="flex items-center gap-1.5 py-0.5">
                  <RadioGroupItem value={value} id={`status-${value}`} className="h-3.5 w-3.5" />
                  <label htmlFor={`status-${value}`} className="text-xs cursor-pointer">{label}</label>
                </div>
              ))}
            </RadioGroup>
          </div>

          <Separator />

          {/* Seeking Filter (matches public Opportunities page) */}
          <div>
            <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1.5 block">Seeking</Label>
            <div className="space-y-1">
              {SEEKING_COLLABORATION_OPTIONS.map(({ value, label }) => (
                <div key={value} className="flex items-center gap-2">
                  <Checkbox
                    id={`seeking-${value}`}
                    checked={selectedSeeking.includes(value)}
                    onCheckedChange={() => toggleSeeking(value)}
                    className="h-3.5 w-3.5"
                  />
                  <label htmlFor={`seeking-${value}`} className="text-xs cursor-pointer">
                    {label}
                  </label>
                </div>
              ))}
            </div>
          </div>

          <Separator />

          {/* Stage Filter (client-side checkboxes) */}
          <div>
            <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1.5 block">Stage</Label>
            <div className="space-y-1">
              {ALL_STAGES.map((stage) => (
                <div key={stage} className="flex items-center gap-2">
                  <Checkbox
                    id={`stage-${stage}`}
                    checked={selectedStages.includes(stage)}
                    onCheckedChange={() => toggleStage(stage)}
                    className="h-3.5 w-3.5"
                  />
                  <label htmlFor={`stage-${stage}`} className="text-xs cursor-pointer">
                    {STAGE_LABELS[stage]}
                  </label>
                </div>
              ))}
            </div>
          </div>

        </div>
      </ScrollArea>
    </div>
  );

  // ===========================
  // Center Panel — Project List
  // ===========================

  const CenterPanel = (
    <div className="h-full flex flex-col">
      <div className="h-[72px] px-4 flex items-center justify-between border-b border-border bg-muted flex-shrink-0">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
            <Briefcase className="h-5 w-5 text-primary" />
          </div>
          <div>
            <h2 className="font-semibold text-lg">Opportunities</h2>
            <p className="text-sm text-muted-foreground">
              {loading ? 'Loading...' : `${filteredProjects.length} of ${projects.length} opportunities`}
            </p>
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">
        {loading ? (
          Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="flex items-center gap-3 px-6 py-3 border-b border-border">
              <div className="flex-1 min-w-0">
                <Skeleton className="h-4 w-48 mb-1" />
                <Skeleton className="h-3 w-64" />
              </div>
              <Skeleton className="h-5 w-16 rounded-full" />
            </div>
          ))
        ) : filteredProjects.length === 0 ? (
          <div className="p-12 text-center text-muted-foreground">
            <Briefcase className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p className="font-medium">No opportunities found</p>
            <p className="text-sm mt-1">Try adjusting your filters</p>
          </div>
        ) : (
          filteredProjects.map((project) => {
            const statusStyle = STATUS_STYLES[project.status];
            const src = getProjectSourceType(project);
            return (
              <button
                key={project.id}
                onClick={() => handleSelectProject(project)}
                className={`w-full flex items-center gap-3 px-6 py-3 border-b border-border text-left transition-colors hover:bg-muted/50 ${
                  selectedProject?.id === project.id ? 'bg-primary/5 border-l-2 border-l-primary' : ''
                }`}
              >
                <div
                  className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${
                    src === 'NPS' ? 'bg-blue-500/10' : src === 'Industry' ? 'bg-slate-500/10' : 'bg-sky-500/10'
                  }`}
                >
                  {src === 'NPS' ? (
                    <GraduationCap className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                  ) : src === 'Industry' ? (
                    <Briefcase className="h-4 w-4 text-slate-700 dark:text-slate-400" />
                  ) : (
                    <Building2 className="h-4 w-4 text-sky-700 dark:text-sky-400" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm truncate">{project.title}</p>
                  {(project.pocFirstName || project.pocLastName) && (
                    <p className="text-xs text-muted-foreground mt-0.5 truncate">
                      {[project.pocRank, project.pocFirstName, project.pocLastName].filter(Boolean).join(' ')}
                    </p>
                  )}
                </div>
                <span
                  className="text-[10px] px-1.5 py-0.5 rounded-full font-medium flex-shrink-0"
                  style={{
                    backgroundColor: `${statusStyle.color}15`,
                    color: statusStyle.color,
                  }}
                >
                  {statusStyle.label}
                </span>
                <button
                  onClick={(e) => { e.stopPropagation(); setSelectedProject(project); openEditDialog(project); }}
                  className="p-1.5 text-blue-600 hover:bg-blue-50 rounded transition-colors flex-shrink-0"
                  title="Edit opportunity"
                >
                  <Edit className="h-4 w-4" />
                </button>
              </button>
            );
          })
        )}
      </div>
    </div>
  );

  // ===========================
  // Right Panel — Project Detail
  // ===========================
  const visibleProject = selectedProject && filteredProjects.some((p) => p.id === selectedProject.id) ? selectedProject : null;

  const RightPanel = (
    <div className="h-full flex flex-col">
      <div className="h-[72px] px-4 flex items-center justify-between border-b border-border bg-muted flex-shrink-0">
        <div className="flex items-center gap-3 min-w-0 flex-1 mr-3">
          <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
            <Users className="h-5 w-5 text-primary" />
          </div>
          <div className="min-w-0">
            <h2 className="font-semibold text-lg">Opportunity Details</h2>
            <p className="text-sm text-muted-foreground truncate">
              {visibleProject ? visibleProject.title : 'Select a project'}
            </p>
          </div>
        </div>
        {visibleProject && (
          <div className="flex items-center gap-1.5">
            {(visibleProject.status === 'pending' || visibleProject.status === 'rejected') && (
              <Button
                size="sm"
                className="bg-green-600 hover:bg-green-700 h-8 text-xs"
                onClick={handleApprove}
                disabled={actionLoading}
              >
                {actionLoading ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <CheckCircle className="h-3.5 w-3.5 mr-1.5" />}
                Approve
              </Button>
            )}
            {visibleProject.status === 'pending' && (
              <Button
                size="sm"
                variant="destructive"
                className="h-8 text-xs"
                onClick={() => setRejectDialog(true)}
              >
                <XCircle className="h-3.5 w-3.5 mr-1.5" />
                Reject
              </Button>
            )}
          </div>
        )}
      </div>

      <ScrollArea className="flex-1">
        {visibleProject ? (
          <div className="p-6 space-y-5">
            {/* Source Type Badge + Title */}
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Badge
                  variant="outline"
                  className="text-xs text-white border-0"
                  style={{ backgroundColor: isNPS ? '#2563eb' : isIndustry ? '#334155' : '#0369a1' }}
                >
                  {isNPS ? <GraduationCap className="h-3 w-3 mr-1" /> : isIndustry ? <Briefcase className="h-3 w-3 mr-1" /> : <Building2 className="h-3 w-3 mr-1" />}
                  {sourceStyle?.label}
                </Badge>
                {visibleProject.featured && (
                  <Star className="h-3.5 w-3.5 text-yellow-500 fill-yellow-500" />
                )}
              </div>
              <h3 className="text-base font-bold leading-snug">{visibleProject.title}</h3>
              {visibleProject.partner?.name && (
                <p className="text-sm text-muted-foreground mt-1 flex items-center gap-1.5">
                  <Building2 className="h-3.5 w-3.5" />
                  Submitted by <span className="font-medium text-foreground">{visibleProject.partner.name}</span>
                </p>
              )}
            </div>

            {/* Description */}
            {visibleProject.description && (
              <div>
                <Label className="text-sm font-medium mb-2 block">About</Label>
                <p className="text-sm text-foreground leading-relaxed">{visibleProject.description}</p>
              </div>
            )}

            {/* POC Card */}
            {(pocName || pocEmail) && (
              <div className="p-4 bg-card border border-border rounded-lg">
                <Label className="text-sm font-medium mb-2 flex items-center gap-2">
                  <Users className="h-4 w-4 text-primary" />
                  Point of Contact
                </Label>
                <p className="font-medium text-sm">
                  {visibleProject.pocRank && (
                    <span className="text-muted-foreground mr-1">{visibleProject.pocRank}</span>
                  )}
                  {pocName}
                  {visibleProject.pocIsCheckedIn && (
                    <span className="ml-1.5 inline-flex items-center text-[10px] text-green-600 font-medium">
                      <CheckCircle className="h-2.5 w-2.5 mr-0.5" />
                      Checked In
                    </span>
                  )}
                </p>
                {pocEmail && (
                  <a
                    href={`mailto:${pocEmail}`}
                    className="flex items-center gap-2 text-xs text-primary hover:underline mt-2"
                  >
                    <Mail className="h-3 w-3" />
                    {pocEmail}
                  </a>
                )}
              </div>
            )}

            {/* Additional Info — icon rows matching community Opportunities page */}
            <div className="space-y-3">
              {isNPS ? (
                /* NPS: Department, Stage, Funding Status, Classification */
                <>
                  {visibleProject.department && (
                    <div className="flex items-center gap-3 text-sm">
                      <div className="p-2 rounded-md bg-card border border-border">
                        <Building2 className="h-4 w-4 text-primary" />
                      </div>
                      <div>
                        <p className="text-muted-foreground text-xs">Department</p>
                        <p className="font-medium">{visibleProject.department}</p>
                      </div>
                    </div>
                  )}
                  {visibleProject.stage && (
                    <div className="flex items-center gap-3 text-sm">
                      <div className="p-2 rounded-md bg-card border border-border">
                        <Tag className="h-4 w-4 text-primary" />
                      </div>
                      <div>
                        <p className="text-muted-foreground text-xs">Stage</p>
                        <p className="font-medium">{STAGE_LABELS[visibleProject.stage] || visibleProject.stage}</p>
                      </div>
                    </div>
                  )}
                  {visibleProject.fundingStatus && (
                    <div className="flex items-center gap-3 text-sm">
                      <div className="p-2 rounded-md bg-card border border-border">
                        <DollarSign className="h-4 w-4 text-primary" />
                      </div>
                      <div>
                        <p className="text-muted-foreground text-xs">Funding Status</p>
                        <p className="font-medium">{visibleProject.fundingStatus}</p>
                      </div>
                    </div>
                  )}
                  {visibleProject.classification && (
                    <div className="flex items-center gap-3 text-sm">
                      <div className="p-2 rounded-md bg-card border border-border">
                        <Tag className="h-4 w-4 text-primary" />
                      </div>
                      <div>
                        <p className="text-muted-foreground text-xs">Classification</p>
                        <p className="font-medium">{visibleProject.classification}</p>
                      </div>
                    </div>
                  )}
                </>
              ) : (
                /* Military/Gov & Industry: Department/Sponsor, Stage, Funding Status, Classification, Demo/Duration */
                <>
                  {(visibleProject.department || visibleProject.sponsorOrganization) && (
                    <div className="flex items-center gap-3 text-sm">
                      <div className="p-2 rounded-md bg-card border border-border">
                        <Building2 className="h-4 w-4 text-primary" />
                      </div>
                      <div>
                        <p className="text-muted-foreground text-xs">{visibleProject.sponsorOrganization ? 'Organization' : 'Department'}</p>
                        <p className="font-medium">{visibleProject.sponsorOrganization || visibleProject.department}</p>
                      </div>
                    </div>
                  )}
                  {visibleProject.stage && (
                    <div className="flex items-center gap-3 text-sm">
                      <div className="p-2 rounded-md bg-card border border-border">
                        <Tag className="h-4 w-4 text-primary" />
                      </div>
                      <div>
                        <p className="text-muted-foreground text-xs">Stage</p>
                        <p className="font-medium">{STAGE_LABELS[visibleProject.stage] || visibleProject.stage}</p>
                      </div>
                    </div>
                  )}
                  {visibleProject.fundingStatus && (
                    <div className="flex items-center gap-3 text-sm">
                      <div className="p-2 rounded-md bg-card border border-border">
                        <DollarSign className="h-4 w-4 text-primary" />
                      </div>
                      <div>
                        <p className="text-muted-foreground text-xs">Funding Status</p>
                        <p className="font-medium">{visibleProject.fundingStatus}</p>
                      </div>
                    </div>
                  )}
                  {visibleProject.classification && (
                    <div className="flex items-center gap-3 text-sm">
                      <div className="p-2 rounded-md bg-card border border-border">
                        <Tag className="h-4 w-4 text-primary" />
                      </div>
                      <div>
                        <p className="text-muted-foreground text-xs">Classification</p>
                        <p className="font-medium">{visibleProject.classification}</p>
                      </div>
                    </div>
                  )}
                  {(visibleProject.demoSchedule || visibleProject.duration) && (
                    <div className="flex items-center gap-3 text-sm">
                      <div className="p-2 rounded-md bg-card border border-border">
                        <Clock className="h-4 w-4 text-primary" />
                      </div>
                      <div>
                        <p className="text-muted-foreground text-xs">{visibleProject.demoSchedule ? 'Demo Schedule' : 'Duration'}</p>
                        <p className="font-medium">{visibleProject.demoSchedule || visibleProject.duration}</p>
                      </div>
                    </div>
                  )}
                  {visibleProject.location && (
                    <div className="flex items-center gap-3 text-sm">
                      <div className="p-2 rounded-md bg-card border border-border">
                        <MapPin className="h-4 w-4 text-primary" />
                      </div>
                      <div>
                        <p className="text-muted-foreground text-xs">Location</p>
                        <p className="font-medium">{visibleProject.location}</p>
                      </div>
                    </div>
                  )}
                  {visibleProject.deadline && (
                    <div className="flex items-center gap-3 text-sm">
                      <div className="p-2 rounded-md bg-card border border-border">
                        <Calendar className="h-4 w-4 text-primary" />
                      </div>
                      <div>
                        <p className="text-muted-foreground text-xs">Deadline</p>
                        <p className="font-medium">{new Date(visibleProject.deadline).toLocaleDateString()}</p>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>

            <Separator />

            {/* Industry/Military: Requirements + Benefits */}
            {!isNPS && (visibleProject.requirements || visibleProject.benefits) && (
              <>
                {visibleProject.requirements && (
                  <div>
                    <Label className="text-sm font-medium mb-2 block">Requirements</Label>
                    <p className="text-sm leading-relaxed">{visibleProject.requirements}</p>
                  </div>
                )}
                {visibleProject.benefits && (
                  <div>
                    <Label className="text-sm font-medium mb-2 block">Benefits</Label>
                    <p className="text-sm leading-relaxed">{visibleProject.benefits}</p>
                  </div>
                )}
                <Separator />
              </>
            )}

            {/* Research Areas / DOD Alignment */}
            {((isNPS && visibleProject.researchAreas && visibleProject.researchAreas.length > 0) ||
              (!isNPS && ((visibleProject.researchAreas?.length ?? 0) > 0 || (visibleProject.dodAlignment?.length ?? 0) > 0))) && (
              <div>
                <Label className="text-sm font-medium mb-3 block">Research Areas</Label>
                <div className="flex flex-wrap gap-2">
                  {(isNPS ? visibleProject.researchAreas : (visibleProject.researchAreas || visibleProject.dodAlignment))?.map((area, idx) => (
                    <Badge key={idx} variant="outline" className="text-xs">
                      {area}
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            {/* NPS: Technology Focus */}
            {isNPS && visibleProject.technologyFocus && visibleProject.technologyFocus.length > 0 && (
              <div>
                <Label className="text-sm font-medium mb-3 block">Technology Focus</Label>
                <div className="flex flex-wrap gap-2">
                  {visibleProject.technologyFocus.map((t, idx) => (
                    <Badge key={idx} variant="outline" className="text-xs">
                      {TECHNOLOGY_AREA_LABEL_MAP[t] || t}
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            {/* Mission Impact (NPS) / Potential Mission Impact (exhibitor) */}
            {visibleProject.dodImpact && (
              <div>
                <Label className="text-sm font-medium mb-2 block">
                  {isNPS ? 'Mission Impact' : 'Potential Mission Impact'}
                </Label>
                <p className="text-sm leading-relaxed">{visibleProject.dodImpact}</p>
              </div>
            )}

            {/* Seeking */}
            {visibleProject.seeking && visibleProject.seeking.length > 0 && (
              <div>
                <Label className="text-sm font-medium mb-3 block">Seeking</Label>
                <div className="flex flex-wrap gap-2">
                  {visibleProject.seeking.map((s, idx) => (
                    <Badge
                      key={idx}
                      className="text-xs bg-primary/10 text-primary border-0"
                    >
                      {s}
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            {/* Keywords */}
            {visibleProject.keywords && visibleProject.keywords.length > 0 && (
              <div>
                <Label className="text-sm font-medium mb-3 block">Keywords</Label>
                <div className="flex flex-wrap gap-2">
                  {visibleProject.keywords.map((k, idx) => (
                    <Badge key={idx} variant="secondary" className="text-xs">
                      {k}
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            {/* Team Members (NPS) */}
            {visibleProject.students && visibleProject.students.length > 0 && (
              <div>
                <Label className="text-sm font-medium mb-2 block">Team Members</Label>
                <p className="text-sm">{visibleProject.students.join(', ')}</p>
              </div>
            )}

            {/* Timestamps */}
            {visibleProject.createdAt && (
              <div className="text-[10px] text-muted-foreground/60 pt-2 border-t border-border">
                Created {getTimeAgo(visibleProject.createdAt)}
                {visibleProject.updatedAt && visibleProject.updatedAt !== visibleProject.createdAt && (
                  <span> · Updated {getTimeAgo(visibleProject.updatedAt)}</span>
                )}
              </div>
            )}

          </div>
        ) : (
          <div className="h-full flex items-center justify-center text-muted-foreground p-8 pt-24">
            <div className="text-center">
              <Briefcase className="h-12 w-12 mx-auto mb-3 opacity-30" />
              <p className="font-medium">Select an opportunity</p>
              <p className="text-sm mt-1">Click any opportunity to view details</p>
            </div>
          </div>
        )}
      </ScrollArea>
    </div>
  );

  // ===========================
  // Render
  // ===========================

  return (
    <DesktopShell>
      <div className="h-full flex flex-col">
        {/* Page Title Bar */}
        <div className="px-6 py-4 border-b border-border bg-background flex-shrink-0">
          <h1 className="text-2xl font-bold">Opportunities</h1>
        </div>

        <div className="flex-1 overflow-hidden">
          <ThreePanelLayout
            left={FiltersPanel}
            center={CenterPanel}
            right={RightPanel}
            leftWidth="260px"
            equalCenterRight
            connectedPanels={!!visibleProject}
          />
        </div>
      </div>


      {/* Reject Dialog */}
      <Dialog open={rejectDialog} onOpenChange={setRejectDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Reject &ldquo;{selectedProject?.title}&rdquo;?</DialogTitle>
            <DialogDescription>
              The opportunity will be hidden from public view.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectDialog(false)} disabled={actionLoading}>Cancel</Button>
            <Button variant="destructive" onClick={handleReject} disabled={actionLoading}>
              {actionLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <XCircle className="h-4 w-4 mr-2" />}
              Reject
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={editDialog} onOpenChange={setEditDialog}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Opportunity</DialogTitle>
            <DialogDescription>
              Update opportunity details. Changes take effect immediately.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-4">
            {/* Basic Info */}
            <div>
              <Label className="text-sm">Title</Label>
              <Input value={editForm.title} onChange={(e) => setEditForm((f) => ({ ...f, title: e.target.value }))} />
            </div>
            <div>
              <Label className="text-sm">Description</Label>
              <Textarea
                value={editForm.description}
                onChange={(e) => setEditForm((f) => ({ ...f, description: e.target.value.slice(0, 400) }))}
                rows={3}
                maxLength={400}
              />
              <p className={`text-xs mt-1 text-right ${editForm.description.length >= 400 ? 'text-destructive' : 'text-muted-foreground'}`}>
                {editForm.description.length}/400
              </p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-sm">Stage</Label>
                <Select value={editForm.stage} onValueChange={(v) => setEditForm((f) => ({ ...f, stage: v as ProjectStage }))}>
                  <SelectTrigger className="h-9">
                    <SelectValue placeholder="Select stage" />
                  </SelectTrigger>
                  <SelectContent>
                    {ALL_STAGES.map((s) => (
                      <SelectItem key={s} value={s}>{STAGE_LABELS[s]}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-sm">Department</Label>
                <Input value={editForm.department} onChange={(e) => setEditForm((f) => ({ ...f, department: e.target.value }))} />
              </div>
            </div>
            <div>
              <Label className="text-sm">Classification</Label>
              <Select value={editForm.classification || '_none'} onValueChange={(v) => setEditForm((f) => ({ ...f, classification: v === '_none' ? '' : v }))}>
                <SelectTrigger className="h-9">
                  <SelectValue placeholder="Select classification" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="_none">None</SelectItem>
                  {SECURITY_CLASSIFICATIONS.map(({ value, label }) => (
                    <SelectItem key={value} value={value}>{label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Registration Fields */}
            <Separator />
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Collaboration & Research</p>

            <div>
              <Label className="text-sm">Seeking Collaboration</Label>
              <div className="grid grid-cols-2 gap-1.5 mt-1.5">
                {SEEKING_COLLABORATION_OPTIONS.map(({ value, label }) => (
                  <label
                    key={value}
                    className={`flex items-center gap-2 p-1.5 rounded border cursor-pointer text-xs transition-colors ${
                      editForm.seeking.includes(value) ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50'
                    }`}
                  >
                    <Checkbox
                      checked={editForm.seeking.includes(value)}
                      onCheckedChange={() => setEditForm((f) => ({
                        ...f,
                        seeking: f.seeking.includes(value)
                          ? f.seeking.filter((s) => s !== value)
                          : [...f.seeking, value],
                      }))}
                      className="h-3.5 w-3.5"
                    />
                    {label}
                  </label>
                ))}
              </div>
            </div>

            <div>
              <Label className="text-sm mb-2 block">Technology Focus Areas</Label>
              <div className="grid grid-cols-2 gap-1.5 max-h-60 overflow-y-auto border rounded-md p-2">
                {TECHNOLOGY_AREAS.map(({ value, label }) => {
                  const selected = editForm.technologyFocus.includes(value);
                  return (
                    <label key={value} className="flex items-center gap-2 cursor-pointer p-1 rounded hover:bg-muted/50">
                      <Checkbox
                        checked={selected}
                        onCheckedChange={() => setEditForm((f) => ({
                          ...f,
                          technologyFocus: selected
                            ? f.technologyFocus.filter((v) => v !== value)
                            : [...f.technologyFocus, value],
                        }))}
                        className="h-4 w-4"
                      />
                      <span className="text-xs">{label}</span>
                    </label>
                  );
                })}
              </div>
            </div>

            <div>
              <Label className="text-sm">Funding Status</Label>
              <Select value={editForm.fundingStatus || undefined} onValueChange={(v) => setEditForm((f) => ({ ...f, fundingStatus: v }))}>
                <SelectTrigger className="h-9">
                  <SelectValue placeholder="Select funding status" />
                </SelectTrigger>
                <SelectContent>
                  {FUNDING_STATUS_V2.map(({ value, label }) => (
                    <SelectItem key={value} value={value}>{label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-sm">Mission Impact</Label>
              <Textarea value={editForm.dodImpact} onChange={(e) => setEditForm((f) => ({ ...f, dodImpact: e.target.value }))} rows={3} />
            </div>

            <label className="flex items-center gap-2 cursor-pointer">
              <Checkbox
                checked={editForm.featured}
                onCheckedChange={(checked) => setEditForm((f) => ({ ...f, featured: !!checked }))}
                className="h-4 w-4"
              />
              <span className="text-sm font-medium">Featured</span>
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialog(false)} disabled={editSaving}>Cancel</Button>
            <Button onClick={handleEditSave} disabled={editSaving}>
              {editSaving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DesktopShell>
  );
}
